import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { apiService } from "../../services/api";
import { useApp } from "../../contexts/AppContext";
import { t } from "../../utils/localization";
import { Shop, RouteEntity } from "../../types";
import styles from "./Search.module.css";

export default function Search() {
  const {
    state,
    setSearchQuery,
    setSearchResults,
    setSearchOpen,
    setSelectedShop,
    setActiveTabId,
    setCurrentFloor,
    setActiveRoutePoints,
    setSelectedShopReload,
    setTerminalName,
    setCurrentFloorReload,
  } = useApp();
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQueryState] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const routerLocation = useLocation();

  const {
    data: searchResults,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["search", searchQuery, state.language],
    queryFn: () =>
      apiService.searchShops({ query: searchQuery, language: state.language }),
    enabled: searchQuery.length > 0 && isModalOpen,
    staleTime: 30000,
  });

  useEffect(() => {
    if (searchResults) {
      // Only update if results actually changed to prevent infinite loops
      const currentResults = state.searchResults;
      const resultsChanged =
        searchResults.length !== currentResults.length ||
        searchResults.some(
          (result, index) => result.id !== currentResults[index]?.id
        );

      if (resultsChanged) {
        setSearchResults(searchResults);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchQuery(value);
  };

  const handleSearchClick = () => {
    if (inputValue.trim().length > 0) {
      setSearchQueryState(inputValue.trim());
      setIsModalOpen(true);
      setSearchOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSearchOpen(false);
  };

  const handleResultClick = (shop: Shop) => {
    // Get tabId from shop or from its category
    let shopTabId: number | undefined = shop.tabId;

    if (!shopTabId) {
      // Try to get tabId from categories
      const shopCategories =
        shop.categories || (shop.category ? [shop.category] : []);
      if (shopCategories.length > 0 && shopCategories[0].tabId) {
        shopTabId = shopCategories[0].tabId;
      }
    }

    if (!shopTabId) {
      console.warn("Cannot determine tabId for shop:", shop.id);
      // Still set the shop and close search, but don't switch tabs
      setSelectedShop(shop);
      handleModalClose();
      setInputValue("");
      setSearchQuery("");
      setSearchQueryState("");
      return;
    }

    // Create a shop object with tabId set for TabContent logic
    const shopWithTabId: Shop = {
      ...shop,
      tabId: shopTabId,
    };

    // Set selected shop and switch to the tab
    setSelectedShop(shopWithTabId);
    setActiveTabId(String(shopTabId));
    handleModalClose();
    setInputValue("");
    setSearchQuery("");
    setSearchQueryState("");
  };

  // Handle "More details" button click - аналогично LowerSection
  const handleMoreDetails = (shop: Shop) => {
    // Get tabId from shop or from its category
    let shopTabId: number | undefined = shop.tabId;

    if (!shopTabId) {
      // Try to get tabId from categories
      const shopCategories =
        shop.categories || (shop.category ? [shop.category] : []);
      if (shopCategories.length > 0 && shopCategories[0].tabId) {
        shopTabId = shopCategories[0].tabId;
      }
    }

    if (!shopTabId) {
      console.warn("Cannot determine tabId for shop:", shop.id);
      return;
    }

    // Create a shop object with tabId set for TabContent logic
    const shopWithTabId: Shop = {
      ...shop,
      tabId: shopTabId,
    };

    // Set selected shop and switch to the tab
    setSelectedShop(shopWithTabId);
    setActiveTabId(String(shopTabId));
    handleModalClose();
    setInputValue("");
    setSearchQuery("");
    setSearchQueryState("");
  };

  const handleClear = () => {
    setInputValue("");
    setSearchQuery("");
    setSearchQueryState("");
    setSearchOpen(false);
    setSearchResults([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  return (
    <>
      <div className={styles.searchContainer}>
        <div className={styles.searchInputWrapper}>
          <input
            id="searchInput"
            type="text"
            className={styles.searchInput}
            placeholder={t("search", state.language)}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          {inputValue.length > 0 && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            className={styles.searchIconButton}
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <svg
              className={styles.searchIcon}
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleModalClose}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {state.language === "en"
                  ? "SEARCH RESULTS"
                  : "РЕЗУЛЬТАТЫ ПОИСКА"}
              </h2>
              <button
                className={styles.modalCloseButton}
                onClick={handleModalClose}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className={styles.modalResults}>
              {isLoading && (
                <div className={styles.loadingSpinner}>
                  {t("loading", state.language)}...
                </div>
              )}

              {error && (
                <div className={styles.errorMessage}>
                  {t("error", state.language)}
                </div>
              )}

              {searchResults &&
                searchResults.length === 0 &&
                searchQuery.length > 0 && (
                  <div className={styles.noResults}>
                    {t("noResults", state.language)}
                  </div>
                )}

              {searchResults && searchResults.length > 0 && (
                <>
                  {searchResults.map((shop) => (
                    <div key={shop.id} className={styles.searchResultItem}>
                      <div className={styles.resultLocation}>
                        <span className={styles.resultLocationFloor}>
                          {shop.floor}{" "}
                          {state.language === "en" ? "floor" : "этаж"}
                        </span>
                        <span className={styles.resultLocationLocation}>
                          {state.language === "en"
                            ? shop.locationEn && shop.locationEn.trim() !== ""
                              ? shop.locationEn
                              : shop.locationRu
                            : shop.locationRu}
                        </span>
                      </div>
                      <div className={styles.resultContent}>
                        <div className={styles.resultLeft}>
                          <div className={styles.logoAndHours}>
                            {shop.logoUrl && (
                              <div className={styles.shopLogoBox}>
                                <img
                                  src={
                                    shop.logoUrl
                                      ? `${process.env.REACT_APP_MEDIA_PATH}${shop.logoUrl}`
                                      : "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23FAA30D'/%3E%3Cstop offset='0.5' stop-color='%23F76708'/%3E%3Cstop offset='1' stop-color='%23EB0C4F'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                                  }
                                  alt={
                                    state.language === "en"
                                      ? shop.nameEn || shop.nameRu
                                      : shop.nameRu
                                  }
                                  className={styles.shopLogo}
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                            {shop.workingHoursRu && (
                              <div className={styles.shopWorkingHours}>
                                {(state.language === "en"
                                  ? shop.workingHoursEn &&
                                    shop.workingHoursEn.trim() !== ""
                                    ? shop.workingHoursEn
                                    : shop.workingHoursRu
                                  : shop.workingHoursRu
                                )
                                  .split("\n")
                                  .map((line, index) => (
                                    <div key={index}>{line}</div>
                                  ))}
                              </div>
                            )}
                          </div>
                          <div className={styles.shopInfo}>
                            <div className={styles.shopName}>
                              {state.language === "en"
                                ? shop.nameEn && shop.nameEn.trim() !== ""
                                  ? shop.nameEn
                                  : shop.nameRu
                                : shop.nameRu}
                            </div>
                            <div className={styles.shopDescription}>
                              {state.language === "en"
                                ? shop.shortDescriptionEn &&
                                  shop.shortDescriptionEn.trim() !== ""
                                  ? shop.shortDescriptionEn
                                  : shop.shortDescriptionRu
                                : shop.shortDescriptionRu}
                            </div>
                          </div>
                        </div>
                        <div className={styles.resultRight}>
                          <button
                            className={styles.buildRouteButton}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!shop) return;
                              const params = new URLSearchParams(
                                routerLocation.search
                              );
                              const terminalName = params.get("terminalName");
                              try {
                                // Resolve terminal by name
                                const terminals = await apiService.getTerminals(
                                  {
                                    active: true,
                                  }
                                );
                                const terminal = terminalName
                                  ? terminals.find(
                                      (t) => t.name === terminalName
                                    )
                                  : undefined;
                                if (!terminal) {
                                  setActiveRoutePoints(undefined);
                                  handleModalClose();
                                  return;
                                }
                                // Find route for terminal+shop
                                const routes = await apiService.getRoutes();
                                const match = routes.find(
                                  (r) =>
                                    String(r.shopId) === String(shop.id) &&
                                    String(r.terminalId) === String(terminal.id)
                                );
                                if (
                                  match &&
                                  match.points &&
                                  match.points.length > 0
                                ) {
                                  // Set selected shop first so clearing effect runs before we apply the message
                                  setSelectedShop(shop);
                                  // Save route data for reload
                                  setSelectedShopReload(shop);
                                  setTerminalName(terminalName || undefined);
                                  setCurrentFloorReload(terminal.floor);
                                  console.log(
                                    "Route saved for reload (Search modal):",
                                    {
                                      shop: shop.id,
                                      terminalName,
                                      floor: terminal.floor,
                                    }
                                  );
                                  // Open the floor where the terminal is located
                                  if (state.currentFloor !== terminal.floor) {
                                    setCurrentFloor(terminal.floor);
                                  }
                                  // Switch to map tab
                                  const mapTab = state.tabs?.find(
                                    (tab) => parseInt(tab.id) === 1
                                  );
                                  if (mapTab) {
                                    setActiveTabId(mapTab.id);
                                  }
                                  // Set route data after selectedShop effect finishes clearing previous state
                                  setTimeout(() => {
                                    setActiveRoutePoints(match.points.slice());
                                  }, 50);
                                } else {
                                  setActiveRoutePoints(undefined);
                                }
                                handleModalClose();
                              } catch (e) {
                                console.error("Error building route:", e);
                                setActiveRoutePoints(undefined);
                                handleModalClose();
                              }
                            }}
                          >
                            {state.language === "en"
                              ? "BUILD ROUTE"
                              : "ПОСТРОИТЬ МАРШРУТ"}
                          </button>
                          <button
                            className={styles.moreDetailsButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoreDetails(shop);
                            }}
                          >
                            {state.language === "en"
                              ? "MORE DETAILS"
                              : "ПОДРОБНЕЕ"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
